/*
 * pcx-capture-adapter.js — the SINGLE Shopify-glue file for the diagnostics
 * capture modules (spec replay-v2 R6 §c).
 *
 *   The capture modules (pcx-capture-web-vitals.js, pcx-capture-errors.js,
 *   pcx-capture-form-field.js) are deliberately theme-agnostic — zero
 *   Shopify/Liquid dependencies, fully portable to a future Halide build.
 *   This adapter is the ONLY place that knows about:
 *     - the Shopify App Proxy endpoints (/apps/events/collect, /apps/events/error-collect)
 *     - window.PeterCox (the Liquid-rendered per-page context bootstrap)
 *     - the same-origin keepalive POST shape Shopify's edge HMAC-signs
 *
 *   To port to Halide: rewrite ONLY this file (new endpoints + context source);
 *   the three capture modules move across unchanged.
 *
 *   Loaded with `defer` after the capture modules, from
 *   snippets/custom-event-tracker.liquid.
 */
(function () {
  'use strict';

  var COLLECT_URL = '/apps/events/collect';
  var ERROR_COLLECT_URL = '/apps/events/error-collect';

  window.PeterCox = window.PeterCox || {};
  var Capture = window.PeterCoxCapture;
  if (!Capture) return;   // modules not present — nothing to wire

  // ── Shopify glue: the send sinks ─────────────────────────────────────────
  // Same-origin keepalive POST; Shopify's App Proxy edge HMAC-signs it so the
  // collector trusts it. JSON body, same shape the funnel tracker uses.
  function postJson(url, body) {
    try {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true
      });
    } catch (e) {
      if (window.console && console.warn) console.warn('pcx-capture send error', e);
    }
  }

  // web_vitals / form events ride the existing /collect envelope as regular
  // events (the funnel tracker's track() shape). Reuse it if present, else a
  // minimal local sender.
  function track(eventType, properties) {
    if (window.PeterCox && typeof window.PeterCox.track === 'function') {
      return window.PeterCox.track(eventType, properties);
    }
    return postJson(COLLECT_URL, { event_type: eventType, properties: properties || {} });
  }

  // Identity enrichment lives HERE, in the Shopify-glue adapter — never in the
  // portable capture modules (spec R6 §c forbids window.PeterCox in them, and
  // the R6 #33 portability test enforces it). The funnel tracker
  // (pcx-tracker.js) owns the cookie-backed visitor/session ids; the errors
  // module only knows {entries, page_path}. Read the SAME ids the /collect path
  // uses via the exposed _ensure* helpers so error rows join 1:1 to the
  // session's funnel events. Returns null when unavailable (helper missing) so
  // we never attach a bogus key.
  function identity() {
    var t = window.PeterCox && window.PeterCox.tracker;
    if (!t) return null;
    var vid = (typeof t._ensureVisitorId === 'function') ? t._ensureVisitorId() : null;
    var sid = (typeof t._ensureSessionId === 'function') ? t._ensureSessionId() : null;
    if (!vid && !sid) return null;
    return { visitor_id: vid, session_id: sid };
  }

  // Error hygiene rides the dedicated /error-collect sibling endpoint. Enrich
  // the module's {entries, page_path} payload with visitor_id/session_id
  // additively — entries and page_path pass through unchanged.
  function sendErrors(payload) {
    var body = payload || {};
    var id = identity();
    if (id) {
      body.visitor_id = id.visitor_id;
      body.session_id = id.session_id;
    }
    return postJson(ERROR_COLLECT_URL, body);
  }

  var pagePath = (window.location && window.location.pathname) || null;

  // ── Wire web vitals ──────────────────────────────────────────────────────
  if (Capture.webVitals && window.webVitals) {
    Capture.webVitals.init({
      track: track,
      vitals: window.webVitals,
      navigationType: (window.PeterCox && window.PeterCox.navigationType) || null
    });
  }

  // ── Wire error capture ───────────────────────────────────────────────────
  if (Capture.errors) {
    Capture.errors.init({
      send: sendErrors,
      pagePath: pagePath
      // maxEntries / messageMaxChars left at module defaults (30 / 1000),
      // which match the collector caps.
    });
  }

  // ── Wire form-field analytics ────────────────────────────────────────────
  if (Capture.formField) {
    Capture.formField.init({
      track: track,
      // Track the two known forms; the module also accepts any form carrying
      // data-pcx-form. Field NAMES only — see the module's masking invariant.
      forms: (window.PeterCox && window.PeterCox.trackedForms) || ['newsletter', 'contact']
    });
  }
})();
